import React, { useMemo } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { Lock, ArrowRight, Sparkles } from "lucide-react";
import FullPageLoader from "../loaders/FullPageLoader";
import { useAddonAccessChecker } from "../../hooks/useAddonAccess";
import { useCrmPath } from "../../hooks/useCrmPath";

// Human-readable names for each addon key. Used by the gate panel so
// the user sees "Social Media Suite isn't in your plan" instead of
// "agentSocialMediaSuite isn't in your plan." Keeps the gate
// self-contained — no need to plumb a displayName through every Route.
const ADDON_LABELS = {
  // Keys MUST match the addonKey values used on the /addon/* routes in
  // App.jsx — a mismatch falls through to the de-camelizer, which
  // renders initialisms like "Agent C M A Builder".
  agentSocialMediaSuite: "Social Media Suite",
  agentCMABuilder: "CMA Report Builder",
  agentClientPortalPro: "Client Portal Pro",
  wholesaleDirectMailBlaster: "Direct Mail Blaster",
  wholesaleBuyerMatchmaker: "Buyer Matchmaker",
  wholesaleCompsEngine: "Comps & ARV Engine",
  probateDirectMailEngine: "Probate Direct Mail",
  probateCourtMonitor: "Court Event Tracker",
  probateOfferGenerator: "Offer Generator",
  flipperDealFinder: "Deal Finder",
  flipperLenderConnect: "Lender Hub",
  flipperListingLaunchpad: "Listing Launchpad",
  // Legacy/alternate keys some callers still pass:
  agentBrandingKit: "Branding Kit",
  agentMarketingTools: "Marketing Tools",
  agentLeadGenSuite: "Lead Gen Suite",
  voiceDialer: "Voice Dialer",
  openHouseToolkit: "Open House Toolkit",
  listingLaunchpad: "Listing Launchpad",
  cmaBuilder: "CMA Builder",
  dealFinder: "Deal Finder",
  compsEngine: "Comps Engine",
  agentWebsite: "Agent Website",
};

function prettyAddonName(key) {
  return ADDON_LABELS[key] || key.replace(/([A-Z])/g, " $1").replace(/^./, (m) => m.toUpperCase()).trim();
}

const AddonGate = ({
  addonKey,
  requiredAddons,
  requireAll = false,
  category,
  fallbackPath, // optional — if set, redirect there instead of showing inline panel
  requireAuth = true,
  children,
}) => {
  const { canAccess, loading, user } = useAddonAccessChecker();
  const location = useLocation();

  const addonKeys = useMemo(() => {
    if (Array.isArray(requiredAddons) && requiredAddons.length > 0) {
      return requiredAddons;
    }
    if (addonKey) {
      return [addonKey];
    }
    return [];
  }, [addonKey, requiredAddons]);

  // Admins always have access to all addon pages
  const isAdmin = user?.role === "admin";

  const hasAccess = useMemo(() => {
    if (isAdmin) {
      return true;
    }
    if (!addonKeys.length) {
      return true;
    }
    if (requireAll) {
      return addonKeys.every((key) => canAccess(key, { category }));
    }
    return addonKeys.some((key) => canAccess(key, { category }));
  }, [addonKeys, canAccess, category, requireAll, isAdmin]);

  if (loading) {
    return <FullPageLoader />;
  }

  if (requireAuth && !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!hasAccess) {
    // Demo tours get a live preview of every add-on — visitors need to
    // SEE what they'd be buying. A persistent banner makes clear it's a
    // paid add-on, not part of any plan tier, and all writes inside the
    // pages stay blocked via useDemoGuard.
    if (user?.isDemoAccount) {
      return (
        <>
          <AddonPreviewBanner addonKeys={addonKeys} />
          {children}
        </>
      );
    }
    // Keep the silent-redirect behavior available for callers that
    // explicitly want it (e.g. middleware-style boundary checks). New
    // default behavior is to render an inline "addon required" panel
    // so the user knows WHY they can't see the feature instead of
    // being mysteriously punted to the landing page.
    if (fallbackPath) {
      return <Navigate to={fallbackPath} replace />;
    }
    return <AddonRequiredPanel addonKeys={addonKeys} isDemo={false} />;
  }

  return <>{children}</>;
};

function AddonPreviewBanner({ addonKeys }) {
  const names = addonKeys.map(prettyAddonName);
  return (
    <div className="sticky top-0 z-40 flex items-center gap-2 bg-violet-500/15 border-b border-violet-500/40 px-4 py-2.5 backdrop-blur">
      <span className="text-[9px] font-bold tracking-wider bg-violet-500/20 text-violet-300 border border-violet-500/40 px-1.5 py-0.5 rounded-full whitespace-nowrap">ADD-ON PREVIEW</span>
      <p className="text-xs text-violet-200/90">
        <strong>{names.join(" + ")}</strong> is a paid add-on — not included in Starter, Power, or Pro.
        You're seeing a preview; on a real plan you'd switch it on from Settings for a monthly fee.
      </p>
    </div>
  );
}

function AddonRequiredPanel({ addonKeys, isDemo }) {
  // Wholesale, probate and flipper all have add-ons of their own, so
  // this panel must send them back into the CRM they came from.
  const crmPath = useCrmPath();
  const names = addonKeys.map(prettyAddonName);
  const headline = names.length === 1
    ? `${names[0]} isn't in your plan yet`
    : `These add-ons aren't in your plan yet: ${names.join(", ")}`;
  return (
    <div className="min-h-screen bg-[var(--dark-bg)] flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full bg-[var(--lighter-dark)] border border-[var(--primary-color)]/30 rounded-2xl p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-[var(--primary-color)]/15 flex items-center justify-center mx-auto mb-4">
          <Lock className="w-6 h-6 text-[var(--primary-color)]" />
        </div>
        <h1 className="text-xl font-bold text-white mb-2">{headline}</h1>
        <p className="text-gray-400 text-sm mb-6">
          {isDemo
            ? "Add-ons ride on top of a paid plan — the demo tours the core CRM. When you pick a plan, add-ons can be switched on or off any time from Settings."
            : "Add it on from your plan settings to unlock this feature. Add-ons can be turned on or off any time."}
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Link
            to={`${crmPath}/settings?tab=plan`}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[var(--primary-color)] hover:brightness-110 text-black font-semibold rounded-lg text-sm"
          >
            <Sparkles className="w-4 h-4" /> Manage add-ons
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to={`${crmPath}/dashboard`}
            className="inline-flex items-center justify-center px-5 py-2.5 border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 rounded-lg text-sm"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

export default AddonGate;
